import React, { useState } from "react";
import { Bot, CheckCircle2, AlertTriangle, Clock, Loader2, Hand, RefreshCw } from "lucide-react";
import * as api from "../../api";
import { autoReconcileLabel } from "../../lib/autoReconcile";

const STATE_STYLE = {
  CONFIRMED: { cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", icon: CheckCircle2, label: "Recarga confirmada automáticamente" },
  SIMULATED: { cls: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10", icon: Bot, label: "Simulada (DRY_RUN)" },
  EXECUTING: { cls: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10", icon: Loader2, label: "Procesando recarga" },
  RECONCILING: { cls: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10", icon: Loader2, label: "Procesando · confirmando" },
  READY_FOR_PROVIDER: { cls: "text-indigo-300 border-indigo-500/40 bg-indigo-500/10", icon: Bot, label: "Lista para el proveedor" },
  WAITING_PROMO_DECISION: { cls: "text-amber-300 border-amber-500/40 bg-amber-500/10", icon: Clock, label: "Esperando decisión de promoción" },
  MANUAL_REQUIRED: { cls: "text-zinc-300 border-[var(--ag7-border)] bg-ag7-surface-2/40", icon: Hand, label: "Recarga manual" },
  CANCELLED_MANUAL: { cls: "text-zinc-300 border-[var(--ag7-border)] bg-ag7-surface-2/40", icon: Hand, label: "Entregada manualmente · automatización anulada" },
  NOT_ELIGIBLE: { cls: "text-zinc-300 border-[var(--ag7-border)] bg-ag7-surface-2/40", icon: Hand, label: "No automatizable" },
  UNKNOWN: { cls: "text-orange-300 border-orange-500/40 bg-orange-500/10", icon: Loader2, label: "Procesando · confirmando" },
  MANUAL_REVIEW: { cls: "text-orange-300 border-orange-500/40 bg-orange-500/10", icon: AlertTriangle, label: "Requiere revisión humana" },
  FAILED: { cls: "text-red-300 border-red-500/40 bg-red-500/10", icon: AlertTriangle, label: "Falló: revisar" },
  BLOCKED: { cls: "text-red-300 border-red-500/40 bg-red-500/10", icon: AlertTriangle, label: "Bloqueada por una protección" },
};

const RECON_LABEL = {
  ACTIVE: "activa (sondeo ≤ 5 min)",
  COMPLETED: "finalizada",
  STOPPED_MANUALLY: "detenida manualmente",
  TIMEOUT: "agotada sin confirmación",
};

const maskUid = (value) => {
  const raw = String(value || "");
  if (raw.length <= 4) return "*".repeat(raw.length);
  return `${raw.slice(0, 2)}${"*".repeat(Math.max(raw.length - 6, 2))}${raw.slice(-4)}`;
};

export const OrderAutomationItems = ({ order, showToast, onChanged }) => {
  const [busyId, setBusyId] = useState(null);
  const items = (order?.items || []).filter((i) => i?.provider_item_status);
  if (!items.length) return null;

  const automate = async (item) => {
    setBusyId(item.id);
    try {
      const res = await api.paAutomateItem(order.id, item.id);
      const status = res?.operation?.status;
      showToast?.(
        res?.rejected ? `No se pudo ejecutar: ${res.rejected.message}` : `Automatización: ${status}`,
        res?.rejected ? "error" : "success",
      );
      await onChanged?.();
    } catch (e) {
      showToast?.(e?.message || "No se pudo automatizar el item", "error");
    } finally {
      setBusyId(null);
    }
  };

  const retry = async (item) => {
    setBusyId(item.id);
    try {
      const res = await api.paRetry(item.provider_operation_id);
      const status = res?.operation?.status;
      showToast?.(status === "CONFIRMED" ? "Recarga confirmada por el proveedor"
        : status === "UNKNOWN" ? "Recarga enviada · confirmando con el proveedor"
        : `Reintento ejecutado · ${status}`,
        ["CONFIRMED", "UNKNOWN"].includes(status) ? "success" : "error");
    } catch (e) {
      showToast?.(e?.message || "No se pudo reintentar la recarga", "error");
    } finally {
      await onChanged?.();      // el estado real manda: nunca se deja la UI desfasada
      setBusyId(null);
    }
  };

  const resume = async (item) => {
    setBusyId(item.id);
    try {
      const res = await api.paResume(item.provider_operation_id);
      const status = res?.operation?.status;
      showToast?.(status === "CONFIRMED" ? "Plan completado: todos los pasos confirmados"
        : `Plan reanudado · ${status}`, status === "CONFIRMED" ? "success" : "error");
    } catch (e) {
      showToast?.(e?.message || "No se pudo reanudar el plan", "error");
    } finally {
      await onChanged?.();
      setBusyId(null);
    }
  };

  const stopBloodStrikeReconciliation = async (item) => {
    setBusyId(item.id);
    try {
      await api.paStopBloodStrikeReconciliation(item.provider_operation_id);
      showToast?.("Conciliación Blood Strike detenida localmente", "success");
      onChanged?.();
    } catch (e) {
      showToast?.(e?.message || "No se pudo detener la conciliación", "error");
    } finally { setBusyId(null); }
  };

  return (
    <div className="mt-3 space-y-2" data-testid={`order-automation-${order.id}`}>
      <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)]">
        Automatización por producto
      </p>
      {items?.map((item) => {
        const pending = item.provider_item_status === "UNKNOWN"
          && item.provider_auto_reconcile && !item.provider_auto_reconcile.finished;
        const state = pending
          ? { cls: "text-sky-300 border-sky-500/40 bg-sky-500/10", icon: Loader2,
            label: "Procesando · confirmando con el proveedor" }
          : STATE_STYLE[item.provider_item_status] || STATE_STYLE.MANUAL_REQUIRED;
        const Icon = state.icon;
        // Entregado a mano ("Recarga realizada") o pedido ya completado sin entrega
        // automática → ninguna acción automática se ofrece (el backend también la rechaza).
        const deliveredManually = Boolean(item.deliveredManually)
          || item.provider_item_status === "CANCELLED_MANUAL"
          || (["Completado", "Rechazado"].includes(order?.status)
              && !item.deliveredAutomatically);
        const waiting = item.provider_item_status === "WAITING_PROMO_DECISION"
          && !deliveredManually;
        const bloodStrike = [item.game_slug, item.provider_game_slug, order?.game_slug]
          .some((slug) => ["blood-strike", "blood_strike"].includes(String(slug || "").toLowerCase()));
        const reconciling = item.provider_reconciliation_status === "ACTIVE";
        const routeLabel = item.provider_route === "free_fire_20"
          ? "FF 20%" : item.provider_route === "free_fire_id" ? "FF ID" : "";
        return (
          <div key={item.id} className={`rounded-xl border p-3 ${state.cls}`}
            data-testid={`order-item-automation-${item.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">
                  {item.gameName} · {item.packageName}
                </p>
                <p className="text-[11px] font-mono opacity-80">
                  UID {maskUid(item.playerId)}
                  {item.provider_control_number ? ` · Control ${item.provider_control_number}` : ""}
                  {routeLabel && <span className="text-[9px] uppercase tracking-wide opacity-65"
                    title={`Ruta proveedor: ${routeLabel}`}
                    data-testid={`provider-route-${item.id}`}> · vía {routeLabel}</span>}
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold">
                <Icon className={`w-3.5 h-3.5 ${pending || ["EXECUTING", "RECONCILING"].includes(item.provider_item_status) ? "animate-spin" : ""}`} />
                {state.label}
              </span>
            </div>
            {item.provider_item_reason && (
              <p className="mt-1 text-[11px] opacity-70">{item.provider_item_reason}</p>
            )}
            {item.provider_item_status === "UNKNOWN" && item.provider_auto_reconcile && (
              <p className="mt-1 text-[11px] font-mono inline-flex items-center gap-1.5"
                data-testid={`auto-reconcile-${item.id}`}>
                <Loader2 className={`w-3 h-3 ${item.provider_auto_reconcile.finished ? "" : "animate-spin"}`} />
                {autoReconcileLabel(item.provider_auto_reconcile)}
              </p>
            )}
            {bloodStrike && item.provider_reconciliation_status && (
              <p className="mt-1 text-[10px] font-mono uppercase tracking-wider opacity-80"
                data-testid={`bloodstrike-conciliation-state-${item.id}`}>
                Conciliación: {RECON_LABEL[item.provider_reconciliation_status] || item.provider_reconciliation_status}
                {item.provider_recharge_status ? ` · Recarga: ${item.provider_recharge_status}` : ""}
              </p>
            )}
            {bloodStrike && reconciling && item.provider_operation_id && !deliveredManually && (
              <button onClick={() => stopBloodStrikeReconciliation(item)} disabled={busyId === item.id}
                data-testid={`stop-bloodstrike-reconciliation-${item.provider_operation_id}`}
                className="mt-2 px-3 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-bold inline-flex items-center gap-1.5 disabled:opacity-40">
                {busyId === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                DETENER CONCILIACIÓN
              </button>
            )}
            {["MANUAL_REVIEW", "UNKNOWN"].includes(item.provider_item_status)
              && item.provider_operation_id && !deliveredManually && (
              <button onClick={() => resume(item)} disabled={busyId === item.id}
                data-testid={`resume-item-${item.id}`}
                title="Vuelve a consultar el historial del proveedor (sólo lectura) y, si de verdad falta un paso que nunca se envió, lo pide"
                className="mt-2 px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-bold inline-flex items-center gap-1.5 disabled:opacity-40">
                {busyId === item.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" />}
                CONCILIAR Y COMPLETAR EL PLAN
              </button>
            )}
            {["BLOCKED", "FAILED"].includes(item.provider_item_status)
              && item.provider_operation_id && !deliveredManually
              && !item.provider_control_number && (
              <button onClick={() => retry(item)} disabled={busyId === item.id}
                data-testid={`retry-item-${item.id}`}
                title="El intento anterior no envió nada al proveedor: reintenta sin riesgo de cobro doble"
                className="mt-2 px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-[11px] font-black inline-flex items-center gap-1.5 disabled:opacity-40">
                {busyId === item.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" />}
                REINTENTAR RECARGA AUTOMÁTICA
              </button>
            )}
            {waiting && (
              <button onClick={() => automate(item)} disabled={busyId === item.id}
                data-testid={`automate-item-${item.id}`}
                className="mt-2 px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-bold inline-flex items-center gap-1.5 disabled:opacity-40">
                {busyId === item.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Bot className="w-3.5 h-3.5" />}
                SIN PROMO → RECARGAR AUTOMÁTICAMENTE
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};
